import { SourceDataInitials } from '@/constants/SourceDataInitials';
import CommonFieldDataUtils from '@/models/common/CommonFieldDataUtils';
import CompFinalSourceDatasUtils from '@/models/comp-final/CompFinalSourceDatasUtils';
import ICompFinalData from '@/models/comp-final/ICompFinalData';
import ICompFinalDataSource from '@/models/comp-final/ICompFinalDataSource';
import ICompFinalProperty, { newCompFinalProperty } from '@/models/comp-final/ICompFinalProperty';
import ICompFinalSourceData from '@/models/comp-final/ICompFinalSourceData';
import ICompFinalSourceDatas from '@/models/comp-final/ICompFinalSourceDatas';
import CompAddressUtils from '@/models/comp/CompAddressUtils';
import CompDataUtils from '@/models/comp/CompDataUtils';
import CompLocalVersionUtils from '@/models/comp/CompLocalVersionUtils';
import CompUtils from '@/models/comp/CompUtils';
import IComps from '@/models/comp/IComps';
import ICompData from '@/models/comp/ICompData';
import SourceDataUtils from '@/models/comp/SourceDataUtils';
import ISourceData from '@/models/comp/ISourceData';
import IFeedData from '@/models/comp/IFeedData';
import IUserSettings from '@/models/IUserSettings';
import { FieldNames } from '@/constants/FieldNames';
import { formFieldsStoreV36, store, Getters } from '@/store';
import IFormFieldsAdditionalDetails from '@/models/forms/IFormFieldsAdditionalDetails';
import ICompFinalDataSources from '@/models/comp-final/ICompFinalDataSources';
import GridColumnUtils from '@/utils/GridColumnUtils';
import { getRowVisibilityState } from '@/models/grid/GridRowCellData';

export default {
    /**
     * Converts the given comps to the final comp structure for transmitting to Total (exported).
     * Will export all comps when user settings indicates or only the visited comps.
     * Each comp to be exported should have been back filled, field priorities run and value
     * translations performed prior to conversion.
     * @param comps
     * @param userSettings
     * @returns
     */
    convert(comps: IComps, userSettings: IUserSettings): ICompFinalData {
        const compFinalData: ICompFinalData = [];

        // Get the subject property's address1 & unit number
        // Append the additional custom fields to the subject property
        let subjectAddress1AndUnitNo = '';
        for (const comp of comps) {
            if (CompUtils.isSubject(comp)) {
                subjectAddress1AndUnitNo = CompAddressUtils.combineAddress1AndUnitNo(comp.address);
                break;
            }
        }

        // Need to reset the compNumber for each comp to account for any (gaps) comps not being returned.
        let compNumber = 0;

        for (const comp of comps) {
            const address1AndUnitNo = CompAddressUtils.combineAddress1AndUnitNo(comp.address);

            const compFinalProperty: ICompFinalProperty = newCompFinalProperty();
            if (userSettings.exportAllComps || CompUtils.shouldExport(comp)) {

                CompAddressUtils.copy(comp.address, compFinalProperty.address);

                compFinalProperty.compKey = comp.compKey;
                compFinalProperty.compNumber = compNumber;
                compFinalProperty.compType = comp.compType;
                compFinalProperty.formCode = comp.formCode;
                compFinalProperty.hasVisited = userSettings.exportAllComps === true ? true : comp.hasVisited === true;
                compFinalProperty.propertyPhoto = comp.propertyPhoto;
                compFinalProperty.address1WithUnitNo = address1AndUnitNo;

                // Handle source datas (new instances so as to not disturb the store)
                if (comp.sourceData && comp.sourceData.length > 0) {
                    const finalSourceDatas: ICompFinalSourceDatas = [];
                    for (const compData of comp.sourceData) {
                        // Don't add the source data if one exists by field name
                        if (!CompFinalSourceDatasUtils.existsByFieldName(finalSourceDatas, compData.fieldName)) {
                            const finalSourceData: ICompFinalSourceData = {
                                fieldId: compData.fieldId,
                                fieldName: compData.fieldName,
                                sourceInitial: compData.sourceInitial,
                                value: compData.value,
                            };

                            finalSourceDatas.push(finalSourceData);
                        }
                    }

                    // Manually added or MLS imported comps won't have the ID field.
                    // Add it so TOTAL will have a unique Guid. It's needed to identify the comp for
                    // other processing (i.e. sales history in the addenda).
                    if (!CompFinalSourceDatasUtils.existsByFieldName(finalSourceDatas, FieldNames.Id)) {
                        const finalSourceData: ICompFinalSourceData = {
                            fieldName: FieldNames.Id,
                            sourceInitial: '',
                            value: comp.qsGuid,
                        };

                        finalSourceDatas.push(finalSourceData);
                    }

                    compFinalProperty.sourceData = finalSourceDatas;
                }

                // Handle local versions (new instances so as to not disturb the store)
                if (comp.localVersions && comp.localVersions.length > 0) {
                    for (const compLocalVersion of comp.localVersions) {
                        const finalLocalVersion = CompLocalVersionUtils.duplicate(compLocalVersion);
                        compFinalProperty.localVersions.push(finalLocalVersion);
                    }
                }

                // Handle notes data (new instances so as to not disturb the store)
                if (comp.notesData && comp.notesData.length > 0) {
                    const finalFieldData = CommonFieldDataUtils.duplicate(comp.notesData);
                    compFinalProperty.notesData = finalFieldData;
                }

                // Handle sources (new instances so as to not disturb the store)
                if (comp.sourceDataDB) {
                    const finalDataSource = convertToFinalDataSource(comp.sourceDataDB, SourceDataInitials.Database);
                    compFinalProperty.sources.push(finalDataSource);
                    compFinalProperty.sourceDataDB = finalDataSource;
                }
                if (comp.sourceDataPR) {
                    const finalDataSource = convertToFinalDataSource(comp.sourceDataPR, SourceDataInitials.PublicRecords);
                    compFinalProperty.sources.push(finalDataSource);
                    compFinalProperty.sourceDataPR = finalDataSource;
                }
                if (comp.sourceDataSE) {
                    const finalDataSource = convertToFinalDataSource(comp.sourceDataSE, SourceDataInitials.SmartExchange);
                    compFinalProperty.sources.push(finalDataSource);
                    compFinalProperty.sourceDataSE = finalDataSource;
                }
                if (comp.sourceDataMLS) {
                    const finalDataSource = convertToFinalDataSource(comp.sourceDataMLS, SourceDataInitials.Mls);
                    compFinalProperty.sources.push(finalDataSource);
                    compFinalProperty.sourceDataMLS = finalDataSource;
                }
                if (comp.sourceDataFF) {
                    const finalDataSource = convertToFinalDataSource(comp.sourceDataFF, SourceDataInitials.FormFiller);
                    compFinalProperty.sources.push(finalDataSource);
                    compFinalProperty.sourceDataFF = finalDataSource;
                }

                compFinalData.push(compFinalProperty);

                compNumber++;
            }
        }

        return compFinalData;
    },

    appendAdditionalProperties(compFinalData: ICompFinalData): ICompFinalData {
        const fieldsAdditionalDetails = formFieldsStoreV36.FormFieldsAdditionalDetails as IFormFieldsAdditionalDetails | null;
        const repeatableKeyByFieldName = new Map<string, string>();
        const fieldIdByAdditionalDetailFieldName = new Map<string, number | null>();
        const repeatableKeyByAdditionalDetailFieldName = new Map<string, string>();

        // Set repeatable keys for each fieldname from the additional details.  This will be used to set the repeatable key for each source data.
        if (Array.isArray(fieldsAdditionalDetails)) {
            for (const fieldDetail of fieldsAdditionalDetails) {
                const fieldName = String(fieldDetail?.fieldName || '');
                if (!fieldName) {
                    continue;
                }

                if (!repeatableKeyByFieldName.has(fieldName)) {
                    repeatableKeyByFieldName.set(fieldName, String(fieldDetail?.repeatableKey || ''));
                }

                if (!fieldIdByAdditionalDetailFieldName.has(fieldName)) {
                    fieldIdByAdditionalDetailFieldName.set(fieldName, fieldDetail?.fieldId ?? null);
                }

                if (!repeatableKeyByAdditionalDetailFieldName.has(fieldName)) {
                    repeatableKeyByAdditionalDetailFieldName.set(fieldName, String(fieldDetail?.repeatableKey || ''));
                }
            }
        }

        for (const comp of compFinalData) {
            if (!Array.isArray(comp?.sourceData)) {
                continue;
            }

            const fieldIdByFieldName = new Map<string, number | null>();
            for (const compSourceData of comp.sourceData) {
                const fieldName = String(compSourceData?.fieldName || '');
                if (!fieldName || fieldIdByFieldName.has(fieldName)) {
                    continue;
                }

                fieldIdByFieldName.set(fieldName, compSourceData?.fieldId ?? null);
            }

            const ff = buildKeyValueFromSourcesData(comp.sources.find(s => s.id === SourceDataInitials.FormFiller)?.sourceData || {});
            const db = buildKeyValueFromSourcesData(comp.sources.find(s => s.id === SourceDataInitials.Database)?.sourceData || {});
            const mls = buildKeyValueFromSourcesData(comp.sources.find(s => s.id === SourceDataInitials.Mls)?.sourceData || {});
            const se = buildKeyValueFromSourcesData(comp.sources.find(s => s.id === SourceDataInitials.SmartExchange)?.sourceData || {});
            const pr = buildKeyValueFromSourcesData(comp.sources.find(s => s.id === SourceDataInitials.PublicRecords)?.sourceData || {});

            appendMissingSubjectNestedFields(
                comp.sourceData,
                [
                    { sourceInitial: SourceDataInitials.FormFiller, items: ff },
                    { sourceInitial: SourceDataInitials.Database, items: db },
                    { sourceInitial: SourceDataInitials.Mls, items: mls },
                    { sourceInitial: SourceDataInitials.SmartExchange, items: se },
                    { sourceInitial: SourceDataInitials.PublicRecords, items: pr }
                ],
                fieldIdByAdditionalDetailFieldName,
                fieldIdByFieldName
            );

            let childrenFields: ICompFinalSourceDatas = [];
            const getSourceItems = (sourceInitial: string): Array<{ key: string; value: unknown }> => {
                switch (sourceInitial) {
                    case SourceDataInitials.FormFiller:
                        return ff;
                    case SourceDataInitials.Database:
                        return db;
                    case SourceDataInitials.Mls:
                        return mls;
                    case SourceDataInitials.SmartExchange:
                        return se;
                    case SourceDataInitials.PublicRecords:
                        return pr;
                    default:
                        return [];
                }
            };
            const mapChildrenFields = (
                childFieldnames: string[],
                parentFieldNameIndex: string | null,
                parentFieldId: number | null,
                sourceInitial: string
            ): ICompFinalSourceDatas => {
                const items = getSourceItems(sourceInitial);
                return items
                    .filter(item => childFieldnames.some(child =>
                        item.key === (parentFieldNameIndex ? `${parentFieldNameIndex}-${child}` : child)
                    ))
                    .map(item => {
                        const itemKey = String(item.key);
                        const childFieldName = parentFieldNameIndex && itemKey.startsWith(`${parentFieldNameIndex}-`)
                            ? itemKey.slice(parentFieldNameIndex.length + 1)
                            : itemKey;
                        const resolvedChildFieldId =
                            fieldIdByAdditionalDetailFieldName.get(childFieldName)
                            ?? fieldIdByFieldName.get(itemKey)
                            ?? fieldIdByFieldName.get(childFieldName)
                            ?? null;

                        return {
                            fieldId: appendParentFieldIdDecimalToChildFieldId(resolvedChildFieldId, parentFieldId),
                            repeatableKey:
                                repeatableKeyByAdditionalDetailFieldName.get(childFieldName)
                                ?? repeatableKeyByFieldName.get(itemKey)
                                ?? repeatableKeyByFieldName.get(childFieldName)
                                ?? null,
                            sourceInitial,
                            fieldName: childFieldName,
                            fieldNameFullIndex: itemKey,
                            value: String(item.value ?? ''),
                        };
                    });
            };

            for (const sourceData of comp.sourceData) {
                // Check for a prepended fieldname. ie. "Outbuildings-0-", "Dwellings-0-Units-2-"
                // If found, remove it so we can match the fieldname for its possible repeatable key value.
                // We also need to take the last numeric value from the prepended fieldname and use it as the instance value.
                let fieldName = String(sourceData?.fieldName || '');
                let instance = 0;
                const parentFieldId = sourceData?.fieldId ?? null;

                const lastIndex = fieldName.lastIndexOf('-');
                const fieldNameFullIndex = fieldName;

                // If the fieldname has a hypen, we need to break down the fieldname to find its 
                // children fieldnames (if found), base fieldname and instance.
                if (lastIndex > -1) {
                    const sourceInitial = sourceData?.sourceInitial || '';
                    const field = GridColumnUtils.getNormalizedFieldName(fieldName);

                    // Check for children fields by fieldnameIndex                    
                    const childFieldnames = GridColumnUtils.getParentChildFields(fieldName);

                    if (childFieldnames && childFieldnames.length > 0) {
                        childrenFields.push(...mapChildrenFields(childFieldnames, field.fieldnameIndex, parentFieldId, sourceInitial));
                    }

                    // Set Fieldname to Base Fieldname (remove the prepended part)
                    fieldName = field.baseFieldname;
                } else {
                    // Check for children fields by fieldnameIndex                    
                    const childFieldnames = GridColumnUtils.getParentChildFields(fieldName);
                    if (childFieldnames && childFieldnames.length > 0) {
                        childrenFields.push(...mapChildrenFields(
                            childFieldnames,
                            null,
                            parentFieldId,
                            sourceData?.sourceInitial || ''
                        ));
                    }
                }

                // Set the fieldname and repeatable key for the source data
                sourceData.fieldName = fieldName;
                sourceData.fieldNameFullIndex = fieldNameFullIndex;
                sourceData.repeatableKey = repeatableKeyByFieldName.get(fieldName) || '';
            }

            // Insert the children fields into the source data array
            comp.sourceData.push(...childrenFields);    

        }

        // Filter out all fields that have fieldId: "undefined" and souceInitial: "" OR sourceInitial: "" and value: ""
        // Also filter out any fields the user has toggled to "hidden" via the row visibility eyeball icon,
        // including child fields whose linked parent (concatenated) field was toggled hidden.
        const fieldsLinked: Record<string, string[]> = formFieldsStoreV36.ParentChildFields ?? {};
        const parentFieldNameByChild = new Map<string, string>();
        for (const [parentFieldName, childFieldNames] of Object.entries(fieldsLinked)) {
            for (const childFieldName of childFieldNames) {
                parentFieldNameByChild.set(childFieldName, parentFieldName);
            }
        }

        compFinalData = compFinalData.filter(comp => {
            comp.sourceData = comp.sourceData.filter(sourceData => {
                const isNestedSubjectField = isSubjectNestedFieldPath(
                    String(sourceData?.fieldNameFullIndex || sourceData?.fieldName || '')
                );

                const fieldNameForVisibility = String(sourceData?.fieldNameFullIndex || sourceData?.fieldName || '');
                const rowVisibilityState = getRowVisibilityState(fieldNameForVisibility, formFieldsStoreV36.RowVisibilityGrid);
                if (rowVisibilityState === 'hidden') {
                    return false;
                }

                const parentFieldName = parentFieldNameByChild.get(String(sourceData?.fieldName || ''));
                if (parentFieldName && getRowVisibilityState(parentFieldName, formFieldsStoreV36.RowVisibilityGrid) === 'hidden') {
                    return false;
                }

                // Parent (concatenated display) fields (e.g. DWELL_HEAT) are kept alongside their
                // children (PU_DWELL_HEAT_SYSTEM/PU_DWELL_HEAT_DETAIL) rather than dropped - TOTAL
                // and the Data Facts Sheet need both.

                if (isNestedSubjectField) {
                    return true;
                }

                return !(
                    (sourceData?.fieldId === undefined && sourceData?.sourceInitial === '') ||
                    (sourceData?.sourceInitial === '' && sourceData?.value === '')
                );
            });
            return comp.sourceData.length > 0;
        });


        return compFinalData;
    },

    compFinalDataStructure(compFinalData: ICompFinalData): any {
        const structuredCompFinalData: any[] = [];

        for (const comp of compFinalData) {
            const structuredComp = {
                ...comp,
                //sourceData: buildStructuredSourceData1(comp.sourceData),
                sourceData: buildStructuredSourceData2(comp.sourceData),
            };

            // Reshape each raw feed's flat sourceData into the same nested structure as the comp's main sourceData.
            if (structuredComp.sourceDataDB) {
                structuredComp.sourceDataDB = { ...structuredComp.sourceDataDB, sourceData: buildStructuredFeedSourceData(comp.sourceDataDB?.sourceData) };
            }
            if (structuredComp.sourceDataPR) {
                structuredComp.sourceDataPR = { ...structuredComp.sourceDataPR, sourceData: buildStructuredFeedSourceData(comp.sourceDataPR?.sourceData) };
            }
            if (structuredComp.sourceDataSE) {
                structuredComp.sourceDataSE = { ...structuredComp.sourceDataSE, sourceData: buildStructuredFeedSourceData(comp.sourceDataSE?.sourceData) };
            }
            if (structuredComp.sourceDataMLS) {
                structuredComp.sourceDataMLS = { ...structuredComp.sourceDataMLS, sourceData: buildStructuredFeedSourceData(comp.sourceDataMLS?.sourceData) };
            }
            if (structuredComp.sourceDataFF) {
                structuredComp.sourceDataFF = { ...structuredComp.sourceDataFF, sourceData: buildStructuredFeedSourceData(comp.sourceDataFF?.sourceData) };
            }

            structuredCompFinalData.push(structuredComp);
            
            // Set the hasVisited property for TOTAL to know if the comp was visited or not.  This is needed for TOTAL to know if the comp was visited or not.
            structuredComp.hasVisited = comp.hasVisited === true;

            // Remove the source property data from payload.  This data is not needed
            // by TOTAL and is casting a large payload size.
            delete structuredComp.sources;
        }

        return {
            TotalComps: structuredCompFinalData,
            RowVisibility: formFieldsStoreV36.RowVisibilityReport,
        };
    },
    
};

function convertToFinalDataSource(feedData: IFeedData, sourceInitial: SourceDataInitials): ICompFinalDataSource {
    const result: ICompFinalDataSource = {
        id: sourceInitial,
        sourceInitial: sourceInitial,
        isPeerComp: feedData.isPeerComp,
        peerCount: feedData.peerCount,
        records: feedData.records,
        reportUseCount: feedData.reportUseCount,
        sourceData: [],
        yourCount: feedData.yourCount,
    };

    result.sourceData = SourceDataUtils.duplicate(feedData.sourceData);
    return result;
}

/**
 * Converts a raw feed's flat (hyphenated-key) sourceData lookup into the same nested
 * collection structure produced by buildStructuredSourceData2() for the comp's main sourceData.
 */
function buildStructuredFeedSourceData(sourceData: ISourceData | undefined): Record<string, any> {
    const items: ICompFinalSourceDatas = Object.entries(sourceData || {}).map(([key, value]) => ({
        fieldName: key,
        fieldNameFullIndex: key,
        sourceInitial: '',
        value,
    }));

    return buildStructuredSourceData2(items);
}

function buildKeyValueFromSourcesData(sourcesData: any): any[] {
    const data = [];

    for (const key in sourcesData) {
        if (Object.prototype.hasOwnProperty.call(sourcesData, key)) {
            data.push({
                key: key,
                value: sourcesData[key]
            });
        }
    }

    return data;  
}

function appendParentFieldIdDecimalToChildFieldId(childFieldId: number | null, parentFieldId: number | null): number | null {
    if (childFieldId === null || parentFieldId === null) {
        return childFieldId;
    }

    const parentFieldIdString = String(parentFieldId);
    const decimalIndex = parentFieldIdString.indexOf('.');
    if (decimalIndex === -1) {
        return childFieldId;
    }

    const decimalSuffix = parentFieldIdString.slice(decimalIndex + 1);
    if (!decimalSuffix) {
        return childFieldId;
    }

    const childFieldIdString = String(childFieldId);
    if (childFieldIdString.indexOf('.') > -1) {
        return childFieldId;
    }

    const childFieldIdWithSuffix = Number(`${childFieldIdString}.${decimalSuffix}`);
    return Number.isNaN(childFieldIdWithSuffix) ? childFieldId : childFieldIdWithSuffix;
}

function appendMissingSubjectNestedFields(
    sourceData: ICompFinalSourceDatas,
    sources: Array<{ sourceInitial: SourceDataInitials; items: Array<{ key: string; value: unknown }> }>,
    fieldIdByAdditionalDetailFieldName: Map<string, number | null>,
    fieldIdByFieldName: Map<string, number | null>
): void {
    const collections: Record<string, string> = {
        PROJECT_TYPE: 'ProjectType',
        NATAMLAND: 'NativeAmericanLands',
        SITE_OWNED_IN_COMMON: 'SiteOwnedInCommon',
        HORESPMAINT: 'HomeownerResponsible',
        NEWCONST: 'NewConstruction',
    };

    for (const [parentFieldName, collectionName] of Object.entries(collections)) {
        const flattenedParentFieldName = `${parentFieldName}_FLATTENED`;
        const selectedTransferData = sourceData.find((item) =>
            (item.fieldName === parentFieldName || item.fieldName === flattenedParentFieldName) &&
            String(item.sourceInitial || '').trim() !== '' &&
            item.value !== '' && item.value !== null && item.value !== undefined
        );
        const selectedSourceInitial = String(selectedTransferData?.sourceInitial || '').toLowerCase();
        const orderedSources = [
            ...sources.filter((source) => String(source.sourceInitial).toLowerCase() === selectedSourceInitial),
            ...sources.filter((source) => String(source.sourceInitial).toLowerCase() !== selectedSourceInitial),
        ];
        const childFieldNames = GridColumnUtils.getParentChildFields(flattenedParentFieldName);
        const nestedFieldNames = [flattenedParentFieldName, ...childFieldNames];

        const sourceWithCollection = orderedSources.find((source) =>
            nestedFieldNames.some((nestedFieldName) =>
                source.items.some((item) =>
                    item.key === `${collectionName}-${nestedFieldName}` ||
                    item.key === `${collectionName}-0-${nestedFieldName}`
                )
            )
        );
        if (!sourceWithCollection) {
            continue;
        }

        for (const nestedFieldName of nestedFieldNames) {
            const sourceItem = sourceWithCollection.items.find((item) =>
                item.key === `${collectionName}-${nestedFieldName}` ||
                item.key === `${collectionName}-0-${nestedFieldName}`
            );
            if (!sourceItem || sourceData.some((item) => item.fieldName === sourceItem.key)) {
                continue;
            }

            const value = sourceItem.value ?? '';
            sourceData.push({
                fieldId:
                    fieldIdByAdditionalDetailFieldName.get(nestedFieldName)
                    ?? fieldIdByFieldName.get(nestedFieldName)
                    ?? null,
                fieldName: sourceItem.key,
                sourceInitial: value === '' ? '' : String(sourceWithCollection.sourceInitial).toLowerCase(),
                value: String(value),
            });
        }
    }
}

function buildStructuredSourceData1(sourceDataItems: ICompFinalSourceDatas): Record<string, any> {
    const result: Record<string, any> = {};

    for (const sourceData of sourceDataItems) {
        const fieldNameFullIndex = String(sourceData?.fieldNameFullIndex || sourceData?.fieldName || '');
        const isNestedSubjectField = isSubjectNestedFieldPath(fieldNameFullIndex);

        // Filter out fields with missing identifier.
        if (!isNestedSubjectField && (sourceData?.fieldId === undefined || sourceData?.fieldId === null)) {
            continue;
        }

        if (!fieldNameFullIndex) {
            continue;
        }

        if (fieldNameFullIndex.indexOf('-') === -1) {
            // Flat field — store under a sequential numeric string key directly on result.
            appendNumericLeaf(result, sourceData);
            continue;
        }

        // Hyphenated path — walk into named collection arrays on result.
        insertStructuredSourceData(result, fieldNameFullIndex.split('-'), sourceData);
    }

    return result;
}

function buildStructuredSourceData2(sourceDataItems: ICompFinalSourceDatas): Record<string, any> {
    const result: Record<string, any> = {};

    for (const sourceData of sourceDataItems) {
        const fieldNameFullIndex = String(sourceData?.fieldNameFullIndex || sourceData?.fieldName || '');

        if (!fieldNameFullIndex) {
            continue;
        }

        if (fieldNameFullIndex.indexOf('-') === -1) {
            // Flat field — use the field name as the key and the field value as the value.
            // Parent/concatenated fields (e.g. SC_SOURCE1) and _FLATTENED fields are kept as-is,
            // not dropped in favor of their children - TOTAL and the Data Facts Sheet need both.
            appendFieldValueLeaf2(result, sourceData);
            continue;
        }

        // Hyphenated path — preserve the collection hierarchy while using field/value leaves.
        insertStructuredSourceData2(result, fieldNameFullIndex.split('-'), sourceData);
    }

    // comp.sourceData can retain orphaned indexed entries left over from an earlier canonical
    // row count (e.g. a stale "Amenities-0-OutdoorLiving-0-*" the grid no longer renders because
    // it recomputes row count fresh each build) - export reads sourceData directly, so those
    // never-populated rows would otherwise still show up as an extra blank array entry.
    return pruneBlankArrayEntries(sortStructuredSourceData2(result));
}

function isBlankLeafValue(value: any): boolean {
    if (value === null || value === undefined) {
        return true;
    }
    if (typeof value === 'string') {
        return value.trim() === '';
    }
    if (Array.isArray(value)) {
        return value.every((item) => isBlankLeafValue(item));
    }
    if (typeof value === 'object') {
        return Object.values(value).every((item) => isBlankLeafValue(item));
    }
    return false;
}

function pruneBlankArrayEntries(value: any): any {
    if (Array.isArray(value)) {
        return value
            .map((item) => pruneBlankArrayEntries(item))
            .filter((item) => !isBlankLeafValue(item));
    }
    if (value && typeof value === 'object') {
        const result: Record<string, any> = {};
        for (const [key, entryValue] of Object.entries(value)) {
            result[key] = pruneBlankArrayEntries(entryValue);
        }
        return result;
    }
    return value;
}

function insertStructuredSourceData2(target: Record<string, any>, pathParts: string[], sourceData: ICompFinalSourceData, pathIndex = 0): void {
    const collectionName = pathParts[pathIndex];
    const collectionIndex = pathParts[pathIndex + 1];

    if (!collectionName || collectionIndex === undefined) {
        appendFieldValueLeaf2(target, sourceData, pathParts[pathParts.length - 1]);
        return;
    }

    if (FLAT_OBJECT_COLLECTIONS.has(collectionName)) {
        if (!target[collectionName] || Array.isArray(target[collectionName])) {
            target[collectionName] = {};
        }
        insertStructuredSourceData2(target[collectionName], pathParts, sourceData, pathIndex + 2);
        return;
    }

    const index = Number(collectionIndex);
    if (Number.isNaN(index)) {
        appendFieldValueLeaf2(target, sourceData, pathParts[pathParts.length - 1]);
        return;
    }

    if (!Array.isArray(target[collectionName])) {
        target[collectionName] = [];
    }

    if (!target[collectionName][index]) {
        target[collectionName][index] = {};
    }

    const collectionNode = target[collectionName][index];
    const nextPathIndex = pathIndex + 2;
    const isLeafPath = nextPathIndex >= pathParts.length - 1;

    if (isLeafPath) {
        appendFieldValueLeaf2(collectionNode, sourceData, pathParts[pathParts.length - 1]);
        return;
    }

    insertStructuredSourceData2(collectionNode, pathParts, sourceData, nextPathIndex);
}

function appendFieldValueLeaf2(target: Record<string, any>, sourceData: ICompFinalSourceData, fieldName?: string): void {
    const key = String(fieldName || sourceData?.fieldName || '');
    if (!key) {
        return;
    }

    // Filter out blank values
    const value = sourceData?.value ?? '';
    if (String(value).trim() === '') {
        return;
    }

    target[key] = value;
}

function sortStructuredSourceData2(value: any): any {
    if (Array.isArray(value)) {
        return value.map((item) => sortStructuredSourceData2(item));
    }

    if (!value || typeof value !== 'object') {
        return value;
    }

    const result: Record<string, any> = {};
    const entries = Object.entries(value);
    const stringEntries = entries.filter(([, entryValue]) => typeof entryValue === 'string');
    const indexEntries = entries
        .filter(([, entryValue]) => typeof entryValue !== 'string')
        .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey));

    for (const [key, entryValue] of [...stringEntries, ...indexEntries]) {
        result[key] = sortStructuredSourceData2(entryValue);
    }

    return result;
}

// Collections that should be a flat object rather than an indexed array at the top level.
// Subject nested objects do not contain an array index in their flattened path
// (for example, HomeownerResponsible-HORESPMAINT_YES), so their values belong
// directly under the collection instead of under an invalid NaN array property.
const FLAT_OBJECT_COLLECTIONS = new Set([
    'Amenities',
    'ProjectType',
    'NativeAmericanLands',
    'SiteOwnedInCommon',
    'HomeownerResponsible',
    'NewConstruction',
    'Roof',
]);

function isSubjectNestedFieldPath(fieldName: string): boolean {
    const collectionName = fieldName.split('-')[0];
    return FLAT_OBJECT_COLLECTIONS.has(collectionName) && collectionName !== 'Amenities';
}

function insertStructuredSourceData(target: Record<string, any>, pathParts: string[], sourceData: ICompFinalSourceData, pathIndex = 0): void {
    const collectionName = pathParts[pathIndex];
    const collectionIndex = pathParts[pathIndex + 1];

    if (!collectionName || collectionIndex === undefined) {
        appendNumericLeaf(target, sourceData);
        return;
    }

    const collectionKey = collectionName;

    // AMENITIES (and any other flat-object collections) skip the numeric index level.
    // Their sub-collections sit directly on a plain object instead of array[index].
    if (FLAT_OBJECT_COLLECTIONS.has(collectionKey)) {
        if (!target[collectionKey] || Array.isArray(target[collectionKey])) {
            target[collectionKey] = {};
        }
        insertStructuredSourceData(target[collectionKey], pathParts, sourceData, pathIndex + 2);
        return;
    }

    const index = Number(collectionIndex);

    if (!Array.isArray(target[collectionKey])) {
        target[collectionKey] = [];
    }

    if (!target[collectionKey][index]) {
        target[collectionKey][index] = {};
    }

    const collectionNode = target[collectionKey][index];
    const nextPathIndex = pathIndex + 2;
    const isLeafPath = nextPathIndex >= pathParts.length - 1;

    if (isLeafPath) {
        appendNumericLeaf(collectionNode, sourceData);
        return;
    }

    insertStructuredSourceData(collectionNode, pathParts, sourceData, nextPathIndex);
}

function appendNumericLeaf(target: Record<string, any>, sourceData: ICompFinalSourceData): void {
    let nextIndex = 0;
    while (Object.prototype.hasOwnProperty.call(target, String(nextIndex))) {
        nextIndex++;
    }
    target[String(nextIndex)] = sourceData;
}

function shouldReplaceCompData(existingCompData: ICompData, incomingCompData: ICompData): boolean {
    const existingRank = getCompDataRank(existingCompData);
    const incomingRank = getCompDataRank(incomingCompData);

    if (incomingRank !== existingRank) {
        return incomingRank > existingRank;
    }

    // For ties, prefer the later item to preserve most-recent behavior.
    return true;
}

function getCompDataRank(compData: ICompData): number {
    let rank = 0;
    const sourceInitial = String(compData?.sourceInitial || '').toLowerCase();
    const include = String(compData?.include || '');
    const value = String(compData?.value || '').trim();

    // Explicitly selected rows should win.
    if (include === '1') {
        rank += 1000;
    }

    // User-entered / transfer-applied values should outrank feed values.
    if (sourceInitial === SourceDataInitials.FormFiller) {
        rank += 500;
    }

    // Prefer actual values over blanks.
    if (value !== '') {
        rank += 100;
    }

    return rank;
}
